import uuid from 'uuid/v1';
import { floor } from '../Shared/utils/floatmath';

import {
	WebGivingMerchant,
	ConfirmPaymentRequest,
	CustomFieldEditModel,
	FrequencyCode,
	EndType,
	WebGivingViewModel,
	CustomFieldType,
	ConfirmPaymentRecurringDetails,
	WebGivingPreconfiguredPayment,
	FundVisibility,
	AppContentCardModel,
	AppContentCardType,
	SplitLineItem
} from './webgiving-generated';
import { getNextFirstOrFifteenthAfter } from './utils/date-utils';
import { getLanguageWithFallback } from './utils/language-utils';
import { getApplePayInfo } from './utils/applepay-utils';
import { dateOfGiftForDisplay } from './utils/onetime-gift-utils';

export interface IWebGivingCommonPaymentState {
	amount: number;
	amountIsLocked: boolean;
	calculatedAmountWithFees: number;
	payerElectedToCoverFees: boolean;
	payerEnabledAnniversaryReminder: boolean;
	paymentMethodId: string | null;
	referenceFieldValues: CustomFieldEditModel[];
	paymentToConfirmToken: string | null;
	paymentToConfirmTokens: string[] | null; 
	identifier: string;
	receiptHasBeenSent: boolean | null;
	urlParameters: string | null;
	RecurringSelectorIsVisible?: boolean;
	fundVisibility: FundVisibility;
	fundName: string;
	fundKey: string;
	LineItems: SplitLineItem[];
}

export interface IWebGivingOneTimePaymentState {
	giftType: GiftType.OneTime;
	giftDate: Date;
	isOneTimeFutureGift: boolean;
}

export interface IWebGivingRecurringPaymentState {
	giftType: GiftType.Recurring;
	recurringInfo: IWebGivingRecurringPaymentInfo;
}

export interface IWebGivingRecurringPaymentInfo {
	frequency: FrequencyCode;
	startDate: Date;
	endType: EndType;
	endDate?: Date | null;
	endOccurrences?: number | null;
}

export type IWebGivingPaymentState = IWebGivingCommonPaymentState & (IWebGivingRecurringPaymentState | IWebGivingOneTimePaymentState);

export function defaultPaymentState(
	viewData: WebGivingViewModel,
	usePreconfiguredPaymentDetails: boolean,
	rememberedAmount?: number): IWebGivingPaymentState {
	const { PreconfiguredPaymentDetails: preconfiguredPaymentDetails } = viewData;

	let state: IWebGivingPaymentState = {
		amount: rememberedAmount || 0,
		amountIsLocked: false,
		calculatedAmountWithFees: 0,
		giftType: GiftType.OneTime,
		giftDate: new Date(),
		isOneTimeFutureGift: false,
		payerElectedToCoverFees: viewData.MerchantInfo.PayerCoversFeesDefault,
		payerEnabledAnniversaryReminder: false,
		paymentMethodId: null,
		referenceFieldValues: [],
		paymentToConfirmToken: null,
		paymentToConfirmTokens: null,
		identifier: uuid(),
		receiptHasBeenSent: false,
		urlParameters: null,
		RecurringSelectorIsVisible: true,
		fundVisibility: FundVisibility.Show,
		fundName: '',
		fundKey: '',
		LineItems: []
	};

	let recurringInfo: IWebGivingRecurringPaymentInfo | undefined;

	if (viewData.MerchantInfo.RecurringByDefault) {
		const defaultFrequency = FrequencyCode.Other;
		recurringInfo = {
			endType: EndType.Never,
			startDate: new Date(),
			frequency: defaultFrequency,
		};
	}

	if (viewData.PreconfiguredPaymentDetails && viewData.PreconfiguredPaymentDetails.FundVisibility) {
		state.fundVisibility = viewData.PreconfiguredPaymentDetails.FundVisibility;
	}

	// only preconfigure payment details for the first payment
	if (preconfiguredPaymentDetails && usePreconfiguredPaymentDetails) {
		state.urlParameters = preconfiguredPaymentDetails.UrlParameters;

		if (preconfiguredPaymentDetails.HasRecurringConfiguration) {
			if (preconfiguredPaymentDetails.RecurringFrequency) {
				recurringInfo = {
					frequency: preconfiguredPaymentDetails.RecurringFrequency || FrequencyCode.Monthly,
					startDate: preconfiguredPaymentDetails.RecurringStartDate || new Date(),
					endType: preconfiguredPaymentDetails.RecurringEndType || EndType.Never,
					endDate: preconfiguredPaymentDetails.RecurringEndDate,
					endOccurrences: preconfiguredPaymentDetails.RecurringOccurrences,
				};

				if (recurringInfo.frequency === FrequencyCode.FirstAndFifteenth) {
					recurringInfo.startDate = getNextFirstOrFifteenthAfter(moment(recurringInfo.startDate)).toDate();
				}
			} else {
				recurringInfo = undefined;
			}
		}

		if (preconfiguredPaymentDetails.Amount) {
			const trimmedPreconfiguredAmount = floor(preconfiguredPaymentDetails.Amount, 2);
			state.amount = Math.min(viewData.MerchantInfo.PaymentMax, trimmedPreconfiguredAmount);

			if (preconfiguredPaymentDetails.AmountIsLocked) {
				state.amountIsLocked = preconfiguredPaymentDetails.AmountIsLocked;
			}
		}

		if (preconfiguredPaymentDetails.CustomFields) {
			state.referenceFieldValues = preconfiguredPaymentDetails.CustomFields;
		}

		if (preconfiguredPaymentDetails.GiftDate) {
			state.giftDate = preconfiguredPaymentDetails.GiftDate;
		}
	}

	state.RecurringSelectorIsVisible = (!preconfiguredPaymentDetails || preconfiguredPaymentDetails.RecurringSelectorIsVisible
		|| preconfiguredPaymentDetails.RecurringSelectorIsVisible === null
		|| preconfiguredPaymentDetails.RecurringSelectorIsVisible === undefined)
		? true : preconfiguredPaymentDetails.RecurringSelectorIsVisible;

	if (recurringInfo) {
		state = { ...state, giftType: GiftType.Recurring, recurringInfo: recurringInfo };
	}

	if (preconfiguredPaymentDetails && preconfiguredPaymentDetails.RecurringSuggestionEnabled) {
		state.giftType = GiftType.OneTime;
	}

	return state;
}

export function paymentStateToPaymentRequest(
	preconfiguredPaymentDetails: WebGivingPreconfiguredPayment | null,
	merchant: WebGivingMerchant,
	paymentState: IWebGivingPaymentState,
	userPrefersVoiceCalls: boolean,
	userShouldReceiveWelcomeSMS: boolean,
	termsAndConditionsVersion: string,
	initialQueryParameters: string | null,
	recurringSuggestionId: number | null,
	emailAppContentCardData: AppContentCardModel | null,
	displayedAppContentCardType: AppContentCardType | null,
	hasTranslationEnabled: boolean,
	applePayment?: ApplePayJS.ApplePayPayment)
: ConfirmPaymentRequest {

	let paymentRequest: ConfirmPaymentRequest = {
		LineItems: paymentState.LineItems ||[],
		Amount: paymentState.amount,
		AmountWithFees: paymentState.calculatedAmountWithFees,
		PayerElectedToCoverFees: paymentState.payerElectedToCoverFees,
		PayerEnabledAnniversaryReminder: paymentState.payerEnabledAnniversaryReminder,
		PaymentMethod: paymentState.paymentMethodId as any,
		// TODO this is wrong, there should be an id of WebGivingListing instead of MerchantId
		ListingId: merchant.MerchantId,
		LockVersion: merchant.MerchantVersion,
		Refs: paymentState.referenceFieldValues,
		IsRecurring: paymentState.giftType === GiftType.Recurring,
		IsOneTimeFutureGift: false,
		UrlParameters: paymentState.urlParameters,
		PayerPrefersVoiceCall: userPrefersVoiceCalls,
		ShouldSendWelcomeSMS: userShouldReceiveWelcomeSMS,
		GiftDate: null,
		RecurringDetails: null,
		AdditionalData: preconfiguredPaymentDetails ? preconfiguredPaymentDetails.AdditionalData : '',
		Notes: preconfiguredPaymentDetails ? preconfiguredPaymentDetails.Notes : '',
		ExternalReference: preconfiguredPaymentDetails ? preconfiguredPaymentDetails.ExternalReference : '',
		AdditionalHiddenFundKey: preconfiguredPaymentDetails ? preconfiguredPaymentDetails.AdditionalHiddenFundKey : '',
		IncludeHiddenFundField: preconfiguredPaymentDetails ? preconfiguredPaymentDetails.IncludeHiddenFundField : false,
		TermsAndConditionsVersion: termsAndConditionsVersion,
		InitialUrlParameters: initialQueryParameters,
		RecurringSuggestionId: recurringSuggestionId,
		EmailAppContentCardModel: emailAppContentCardData,
		DisplayedInWebGivingAppContentCardType: displayedAppContentCardType,
		Language: getLanguageWithFallback(hasTranslationEnabled),
		ApplePayInfo: null,
	};

	if (paymentState.giftType === GiftType.OneTime) {
		paymentRequest.GiftDate = paymentState.giftDate;
		paymentRequest.IsOneTimeFutureGift = paymentState.isOneTimeFutureGift;
	}

	if (paymentState.giftType === GiftType.Recurring) {
		const recurringInfo = paymentState.recurringInfo;

		const recurringDetails: ConfirmPaymentRecurringDetails = {
			Frequency: recurringInfo.frequency,
			StartDate: moment(recurringInfo.startDate).toDate(),
			EndType: recurringInfo.endType,
		};

		if (recurringInfo.endType === EndType.Date) {
			recurringDetails.EndDate = moment(recurringInfo.endDate || new Date()).toDate();
		} else if (recurringInfo.endType === EndType.Occurrences) {
			recurringDetails.EndOccurrences = recurringInfo.endOccurrences;
		}

		paymentRequest.RecurringDetails = recurringDetails;
	}

	if (applePayment) {
		paymentRequest.ApplePayInfo = getApplePayInfo(applePayment);
	}

	return paymentRequest;
}

export function clearInvalidCustomFields(merchant: WebGivingMerchant, paymentState: IWebGivingPaymentState) {
	const toRemove: string[] = [];
	paymentState.referenceFieldValues.forEach(field => {
		const { Key, Value } = field;
		const [merchantField] = merchant.CustomFields.filter(f => f.Key === Key);
		if (!merchantField) {
			toRemove.push(field.Key);
			return;
		}

		switch (merchantField.Type) {
			case CustomFieldType.Fund:
				if (!merchantField.Options.some(f => f.Value === Value)) {
					field.Value = merchantField.DefaultOptionValue;
				}
				break;
			case CustomFieldType.DropDown:
				if (!merchantField.Options.some(f => f.Value === Value)) {
					field.Value = merchantField.DefaultOptionValue;
				}
				break;
		}
	});
	paymentState.referenceFieldValues = paymentState.referenceFieldValues.filter(f => !toRemove.some(r => r === f.Key));
}

// Generates the gift date label for displaying using our inbuilt formatter.
// If gift type is recurring or not a one-off future gift, returns empty string.
export const generateOneTimeGiftDateLabel = (paymentState: IWebGivingPaymentState): string => {
	if (paymentState.giftType === GiftType.Recurring || !paymentState.isOneTimeFutureGift) {
		return '';
	}

	return dateOfGiftForDisplay(paymentState.giftDate);
}


export enum GiftType {
	OneTime,
	Recurring,
}